import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem,
  Chip, OutlinedInput, Checkbox, ListItemText, FormControlLabel, Switch, Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import { supplyChainService } from '../services/supplyChainService';

const MAX_LEDGERS_DEFAULT = 20;

function SupplyChainFlow() {
  const [flowData, setFlowData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedLedgers, setSelectedLedgers] = useState([]);
  const [showTransferReceipts, setShowTransferReceipts] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const chartRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadFlowData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod, selectedLedgers, showTransferReceipts]);

  // Auto-toggle based on ledger count: single ledger shows supplier view
  useEffect(() => {
    setShowTransferReceipts(selectedLedgers.length === 1);
  }, [selectedLedgers.length]);

  const loadFilters = async () => {
    try {
      const [periodsData, ledgersData] = await Promise.all([
        supplyChainService.getPeriods(),
        supplyChainService.getLedgers()
      ]);
      setPeriods(periodsData ?? []);
      setLedgers(ledgersData ?? []);
    } catch (err) {
      // Non-fatal
    }
  };

  const loadFlowData = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = {};
      if (selectedPeriod !== 'all') {
        const period = periods.find(p => p.label === selectedPeriod);
        if (period) {
          filters.period_start = period.start;
          filters.period_end = period.end;
        }
      }
      if (selectedLedgers.length > 0) {
        filters.ledger_ids = selectedLedgers;
      } else {
        filters.max_ledgers = MAX_LEDGERS_DEFAULT;
      }
      filters.show_transfer_receipts = showTransferReceipts;
      const data = await supplyChainService.getFlowData(filters);
      setFlowData(data ?? {});
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onChartClick = useCallback((params) => {
    if (params.dataType === 'node' && params.data) {
      const node = params.data;
      if (node.nodeType === 'ledger' && node.e_ledger_id) {
        navigate(`/eledgers?highlight=${node.e_ledger_id}`);
      }
    }
  }, [navigate]);

  const onChartReady = useCallback((chartInstance) => {
    chartRef.current = chartInstance;
  }, []);

  const handleLedgerChange = (event) => {
    const value = event.target.value;
    setSelectedLedgers(typeof value === 'string' ? value.split(',') : value);
  };

  if (loading && !flowData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const hasData = flowData && (flowData?.nodes?.length ?? 0) > 0 && (flowData?.links?.length ?? 0) > 0;
  const metadata = flowData?.metadata || {};

  const colorByType = (node) => {
    switch (node.nodeType) {
      case 'emission_source': return '#ef5350';
      case 'supplier': return '#ff9800';
      case 'ledger': return '#1976d2';
      case 'product': return '#4caf50';
      case 'end_consumer': return '#9e9e9e';
      default: return '#1976d2';
    }
  };

  const chartNodes = hasData ? (flowData?.nodes ?? []).map(node => ({
    ...node,
    itemStyle: { color: colorByType(node) }
  })) : [];

  const chartOptions = hasData ? {
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        if (params.dataType === 'edge') {
          return `${params.data.source} &rarr; ${params.data.target}<br/><strong>${Number(params.data.value).toLocaleString()} kg CO2e</strong>`;
        }
        const node = params.data;
        const typeLabels = {
          emission_source: 'Emission Source',
          supplier: 'Supplier (Scope 3)',
          ledger: 'E-Ledger',
          product: 'Product',
          end_consumer: 'End Consumer'
        };
        const typeLabel = typeLabels[node.nodeType] || '';
        let tip = `<strong>${params.name}</strong>`;
        if (typeLabel) tip += `<br/><em>${typeLabel}</em>`;
        if (node.nodeType === 'ledger') tip += '<br/><span style="color:#1976d2">Click to view ledger</span>';
        return tip;
      }
    },
    series: [{
      type: 'sankey',
      data: chartNodes,
      links: flowData?.links ?? [],
      layoutIterations: 32,
      nodeWidth: 20,
      nodeGap: 14,
      emphasis: {
        focus: 'adjacency'
      },
      lineStyle: {
        color: 'source',
        curveness: 0.5,
        opacity: 0.4
      },
      itemStyle: {
        borderWidth: 1,
        borderColor: '#aaa'
      },
      label: {
        position: 'right',
        fontSize: 11,
        color: '#333'
      }
    }]
  } : null;

  const onEvents = {
    click: onChartClick
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Supply Chain Flow
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            E-ledger carbon flow visualization. Click a ledger node to drill down. Hover to highlight connected flows.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Ledgers</InputLabel>
            <Select
              multiple
              value={selectedLedgers}
              onChange={handleLedgerChange}
              input={<OutlinedInput label="Select Ledgers" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const ledger = ledgers.find(l => l.e_ledger_id === id);
                    return <Chip key={id} label={ledger?.organization_name || id} size="small" />;
                  })}
                </Box>
              )}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              {ledgers.map((ledger) => (
                <MenuItem key={ledger.e_ledger_id} value={ledger.e_ledger_id}>
                  <Checkbox checked={selectedLedgers.indexOf(ledger.e_ledger_id) > -1} />
                  <ListItemText
                    primary={ledger.organization_name}
                    secondary={`${Number(ledger.total_emissions_kg_co2e).toLocaleString()} kg CO2e`}
                  />
                </MenuItem>
              ))}
            </Select>
            {selectedLedgers.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                Showing top {MAX_LEDGERS_DEFAULT} ledgers by emissions. Select specific ledgers to customize.
              </Typography>
            )}
          </FormControl>
        </Grid>

        {periods.length > 0 && (
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Accounting Period</InputLabel>
              <Select
                value={selectedPeriod}
                label="Accounting Period"
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <MenuItem value="all">All Periods</MenuItem>
                {periods.map((p) => (
                  <MenuItem key={p.label} value={p.label}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={3}>
          <Tooltip title="When enabled, shows internal transfers between selected ledgers as supplier inputs (orange). Useful for single-ledger view. Disable for multi-ledger view to avoid duplicate flows.">
            <FormControlLabel
              control={
                <Switch
                  checked={showTransferReceipts}
                  onChange={(e) => setShowTransferReceipts(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Show supplier receipts
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Original Emissions
              </Typography>
              <Typography variant="h6">
                {Number(metadata.total_original_emissions_kg_co2e || 0).toLocaleString()} kg
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                External Transfers
              </Typography>
              <Typography variant="h6">
                {Number(metadata.total_external_transfers_kg_co2e || 0).toLocaleString()} kg
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Ledgers Shown
              </Typography>
              <Typography variant="h6">
                {metadata.ledger_count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {hasData && (
        <Box display="flex" gap={1} mb={1}>
          {[
            { color: '#ef5350', label: 'Emission Source' },
            { color: '#ff9800', label: 'Supplier (S3)' },
            { color: '#1976d2', label: 'E-Ledger' },
            { color: '#4caf50', label: 'Product' },
            { color: '#9e9e9e', label: 'End Consumer' }
          ].map(item => (
            <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', backgroundColor: item.color }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress size={32} />
          </Box>
        ) : hasData ? (
          <ReactECharts
            ref={chartRef}
            option={chartOptions}
            style={{ height: '600px', width: '100%' }}
            notMerge={true}
            onChartReady={onChartReady}
            onEvents={onEvents}
          />
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" p={8}>
            <Typography color="text.secondary">
              No supply chain data available. Run the seed script to populate test data.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default SupplyChainFlow;
